import { z } from "zod";
import { billingCurrencySchema, CURRENCY_META } from "./creditsModel.js";
/**
 * PLANS (migration 0094) — the customer-facing commercial model. A plan bundles JOBS, RESUME
 * scans and AI INTERVIEWS for a price; the customer can buy any quantity and every allowance is
 * pooled at the org level. Nothing here is a plan VALUE — the catalog lives in the `plans` table
 * and is owner-editable. This module is only the arithmetic, and it is the ONE function both the
 * web quote and the server charge use (quotePlan), so the number shown is the number charged.
 *
 * Internally the charge paths still fall back to the legacy credit wallet for orgs that hold
 * credits (creditWallet.spendAllowance), but credits never appear in the customer UI.
 */
export const ALLOWANCE_UNITS = ["job", "scan", "interview"];
/** Customer-facing names — the only vocabulary the customer UI uses for usage. */
export const ALLOWANCE_LABELS = {
    job: { one: "job", many: "jobs" },
    scan: { one: "resume", many: "resumes" },
    interview: { one: "AI interview", many: "AI interviews" },
};
export function allowanceLabel(unit, n) {
    const l = ALLOWANCE_LABELS[unit];
    return `${n.toLocaleString("en-IN")} ${n === 1 ? l.one : l.many}`;
}
// ── Tax ──────────────────────────────────────────────────────────────────────────────────────
export const taxSettingSchema = z.object({
    enabled: z.boolean(),
    name: z.string().min(1).max(20),
    /** percent of the (discounted) price */
    pct: z.number().min(0).max(50),
    /** true = prices INCLUDE the tax (it is split out, not added); false = added on top */
    inclusive: z.boolean().default(false),
    /** currencies the tax applies to (GST → INR only; exports are zero-rated) */
    currencies: z.array(billingCurrencySchema).max(4),
});
export const DEFAULT_TAX = { enabled: true, name: "GST", pct: 18, currencies: ["INR"], inclusive: true };
export function taxPctFor(tax, cur) {
    return tax.enabled && tax.currencies.includes(cur) ? tax.pct : 0;
}
/** Discount for a subtotal, in minor units — capped by max_discount and by the subtotal itself. */
export function couponDiscountMinor(c, subtotalMinor) {
    let d = c.discountType === "percent"
        ? Math.round((subtotalMinor * c.discountValue) / 100)
        : Math.round(c.discountValue);
    if (c.maxDiscountMinor != null)
        d = Math.min(d, c.maxDiscountMinor);
    return Math.max(0, Math.min(d, subtotalMinor));
}
/** Why a coupon can't be applied (customer-readable), or null when it can. */
export function couponRejection(c, ctx) {
    if (!c.isActive)
        return "This coupon isn't active";
    if (c.startsAt && ctx.now < new Date(c.startsAt))
        return "This coupon isn't valid yet";
    if (c.expiresAt && ctx.now > new Date(c.expiresAt))
        return "This coupon has expired";
    if (c.planIds.length > 0 && !c.planIds.includes(ctx.planId))
        return "This coupon doesn't apply to this plan";
    if (c.discountType === "fixed" && c.currency !== ctx.currency)
        return "This coupon isn't valid in your currency";
    if (c.minQty != null && ctx.quantity < c.minQty)
        return `This coupon needs a quantity of at least ${c.minQty}`;
    if (c.minSubtotalMinor != null && ctx.subtotalMinor < c.minSubtotalMinor) {
        return `This coupon needs a subtotal of at least ${formatAmount(c.minSubtotalMinor, ctx.currency)}`;
    }
    if (c.usageLimit != null && ctx.usesTotal >= c.usageLimit)
        return "This coupon has been fully used";
    if (c.perOrgLimit != null && ctx.usesByOrg >= c.perOrgLimit)
        return "You've already used this coupon";
    if (c.firstPurchaseOnly && ctx.orgHasPaidPurchase)
        return "This coupon is for a first purchase only";
    return null;
}
function formatAmount(minor, cur) {
    const m = CURRENCY_META[cur];
    return m.symbol + (minor / m.minor).toLocaleString(m.locale, { maximumFractionDigits: 2 });
}
/**
 * price × qty = subtotal; − coupon = net. Tax-exclusive: total = net + tax(net). Tax-inclusive
 * (0095): total = net, and the tax share inside it is net × pct / (100 + pct). Either way
 * total − tax is the ex-tax amount revenue and partner commission are computed on. All integer
 * minor units, one rounding step each. The server recomputes this at order creation and charges
 * `totalMinor`; it never trusts a client-sent amount.
 */
export function quotePlan(i) {
    const quantity = Math.max(1, Math.floor(i.quantity));
    const subtotalMinor = i.unitPriceMinor * quantity;
    const discountMinor = i.coupon ? couponDiscountMinor(i.coupon, subtotalMinor) : 0;
    const netMinor = subtotalMinor - discountMinor;
    const taxInclusive = i.taxInclusive === true;
    const taxMinor = taxInclusive
        ? Math.round((netMinor * i.taxPct) / (100 + i.taxPct))
        : Math.round((netMinor * i.taxPct) / 100);
    return {
        unitPriceMinor: i.unitPriceMinor,
        quantity,
        subtotalMinor,
        discountMinor,
        netMinor,
        taxPct: i.taxPct,
        taxInclusive,
        taxMinor,
        totalMinor: taxInclusive ? netMinor : netMinor + taxMinor,
        allowances: {
            jobs: i.allowances.jobs * quantity,
            resumes: i.allowances.resumes * quantity,
            interviews: i.allowances.interviews * quantity,
        },
    };
}
// ── Display helpers (customer plan cards) ────────────────────────────────────────────────────
/**
 * The "per AI interview" figure and the "you save vs human interviews" estimate on a plan card.
 * Both are DISPLAY estimates, labelled as such in the UI:
 *   · perInterview = price ÷ interviews (resumes and jobs come on top, so it is an upper bound);
 *   · saving = interviews × the owner-set human-interview cost − price, shown only when positive.
 * null when the plan has no interviews or no price.
 */
export function planValueNote(priceMinor, interviews, humanInterviewMinor) {
    if (interviews <= 0 || priceMinor <= 0)
        return null;
    const perInterviewMinor = Math.round(priceMinor / interviews);
    const saving = humanInterviewMinor != null ? humanInterviewMinor * interviews - priceMinor : null;
    return { perInterviewMinor, savingMinor: saving != null && saving > 0 ? saving : null };
}
/** platform_settings 'human_interview_cost': minor units per currency (owner-set). */
export const humanInterviewCostSchema = z.record(billingCurrencySchema, z.number().int().min(0).max(100_000_000));
// ── Admin + checkout schemas ─────────────────────────────────────────────────────────────────
export const planUpsertSchema = z
    .object({
    name: z.string().trim().min(1).max(80),
    description: z.string().max(300).nullable().optional(),
    currency: billingCurrencySchema,
    priceMinor: z.number().int().min(0).max(1_000_000_000),
    /** struck-out "was" price, display only; must exceed the price to be shown */
    compareAtMinor: z.number().int().positive().max(1_000_000_000).nullable().default(null),
    jobs: z.number().int().min(0).max(100000),
    resumes: z.number().int().min(0).max(10_000_000),
    interviews: z.number().int().min(0).max(1_000_000),
    isCustom: z.boolean(),
    isActive: z.boolean(),
    isFeatured: z.boolean(),
    sortOrder: z.number().int().min(0).max(1000),
    validityMonths: z.number().int().min(1).max(36),
    minQty: z.number().int().min(1).max(1000),
    maxQty: z.number().int().min(1).max(1000),
})
    .refine((p) => p.compareAtMinor == null || p.compareAtMinor > p.priceMinor, {
    message: "The strike-through price must be higher than the price",
    path: ["compareAtMinor"],
})
    .refine((p) => p.minQty <= p.maxQty, { message: "Min quantity can't exceed max quantity", path: ["minQty"] })
    .refine((p) => p.isCustom || p.priceMinor > 0, { message: "A self-serve plan needs a price", path: ["priceMinor"] })
    .refine((p) => p.isCustom || p.jobs + p.resumes + p.interviews > 0, {
    message: "A self-serve plan must include something",
    path: ["resumes"],
});
export const couponUpsertSchema = z
    .object({
    code: z.string().trim().toUpperCase().regex(/^[A-Z0-9_-]{3,32}$/, "3–32 letters, digits, - or _"),
    description: z.string().max(200).nullable().optional(),
    discountType: z.enum(["percent", "fixed"]),
    /** percent, or fixed in MINOR units */
    discountValue: z.number().positive().max(1_000_000_000),
    currency: billingCurrencySchema.nullable(),
    maxDiscountMinor: z.number().int().positive().nullable(),
    minQty: z.number().int().min(1).nullable(),
    minSubtotalMinor: z.number().int().min(0).nullable(),
    startsAt: z.string().datetime({ offset: true }).nullable(),
    expiresAt: z.string().datetime({ offset: true }).nullable(),
    usageLimit: z.number().int().min(1).nullable(),
    perOrgLimit: z.number().int().min(1).nullable(),
    firstPurchaseOnly: z.boolean(),
    isActive: z.boolean(),
    planIds: z.array(z.string().uuid()).max(100),
})
    .refine((c) => c.discountType !== "percent" || c.discountValue <= 100, {
    message: "A percentage can't exceed 100",
    path: ["discountValue"],
})
    .refine((c) => c.discountType !== "fixed" || c.currency != null, {
    message: "A fixed discount needs a currency",
    path: ["currency"],
})
    .refine((c) => !c.startsAt || !c.expiresAt || new Date(c.startsAt) < new Date(c.expiresAt), {
    message: "Expiry must be after the start",
    path: ["expiresAt"],
});
export const planPurchaseSchema = z.object({
    planId: z.string().uuid(),
    quantity: z.number().int().min(1).max(1000),
    couponCode: z.string().trim().toUpperCase().max(32).optional(),
});
//# sourceMappingURL=plans.js.map